import { computed, toValue, unref, watch, type MaybeRef, type MaybeRefOrGetter } from 'vue';

import {
  createEventItemsBreadcrumbDropdownOptions,
  createFormBreadcrumbDropdownOptions,
  createModuleBreadcrumbDropdownOptions,
  createSectionBreadcrumbDropdownOptions,
  createNewsItemsBreadcrumbDropdownOptions,
} from '@/helpers/breadcrumbs.helper';
import { useFormsStore, useBlocksStore, usePagesStore, useProjectStore, useSectionsStore } from '@/stores';
import { Constants } from '@/constants';
import { getLanguageEnabled } from '@/composables/data-display-utils';
import { blockLabelsOf } from '@/composables/useBlockParent';
import { useEventListItemsStore } from '@/stores/eventListItems.store';
import { useNewsItemsStore } from '@/stores/newsItems.store';
import type { BlockParent, BlockRouteFor } from '@/types/modules';

import { storeToRefs } from 'pinia';

export function useSectionBreadcrumbOptions(
  consultationId: MaybeRef<number | undefined>,
  isTemplate: MaybeRef<boolean>,
) {
  const sectionsStore = useSectionsStore();
  const consultationIdRef = computed(() => unref(consultationId));
  const isTemplateRef = computed(() => unref(isTemplate));
  watch(
    consultationIdRef,
    (id) => {
      if (!id) return;
      sectionsStore.fetchSectionsByConsultation(id);
    },
    { immediate: true },
  );
  const sectionOptions = computed(() => {
    const consultationIdValue = consultationIdRef.value;
    if (!consultationIdValue) return [];
    const sections = sectionsStore
      .getSectionsByConsultationId(consultationIdValue)
      .sort((x, y) => x.order_num - y.order_num);
    return createSectionBreadcrumbDropdownOptions(sections, isTemplateRef.value);
  });
  return { sectionOptions };
}

export function useFormBreadcrumbOptions(
  consultationId: MaybeRef<number | undefined>,
  sectionId: MaybeRef<number | undefined>,
  isTemplate: MaybeRef<boolean>,
) {
  const formsStore = useFormsStore();
  const consultationIdRef = computed(() => unref(consultationId));
  const sectionIdRef = computed(() => unref(sectionId));
  const isTemplateRef = computed(() => unref(isTemplate));
  watch(
    sectionIdRef,
    (id) => {
      if (!id) return;
      formsStore.fetchFormsBySection(id);
    },
    { immediate: true },
  );
  const formOptions = computed(() => {
    const consultationIdValue = consultationIdRef.value;
    const sectionIdValue = sectionIdRef.value;
    if (!sectionIdValue) return [];
    const forms = formsStore.getFormsBySectionId(sectionIdValue).sort((x, y) => x.order_num - y.order_num);
    return createFormBreadcrumbDropdownOptions(consultationIdValue, forms, isTemplateRef.value);
  });
  return { formOptions };
}

export function useModuleBreadcrumbOptions(parent: MaybeRefOrGetter<BlockParent>, blockRouteFor: BlockRouteFor) {
  const blocksStore = useBlocksStore();
  const projectStore = useProjectStore();
  const pagesStore = usePagesStore();
  const blockTypes = blocksStore.getTypes();
  watch(
    () => toValue(parent),
    (current) => blocksStore.fetchBlocks(current),
    { immediate: true },
  );
  const moduleOptions = computed(() => {
    const current = toValue(parent);
    const modules = blocksStore.getBlocksByParent(current);
    // This reads the parent the screen has fetched rather than asking useBlockParent, which would fetch it a second
    // time. A Hero can show its project's title, which only a project parent has; a page gives its blocks' languages.
    const project = current.type === Constants.Block.ParentType.Project ? projectStore.getItem(current.id) : undefined;
    const page = current.type === Constants.Block.ParentType.Page ? pagesStore.getItem(current.id) : undefined;
    const stored = project ?? page;
    const languageEnabled = stored ? getLanguageEnabled(stored) : undefined;
    return createModuleBreadcrumbDropdownOptions(
      modules,
      blockRouteFor,
      blockLabelsOf(current.type),
      blockTypes,
      project,
      languageEnabled,
    );
  });
  return { moduleOptions };
}

export function useEventBreadcrumbOptions(
  moduleId: MaybeRef<number | undefined>,
  projectId: MaybeRef<number | undefined>,
) {
  const eventListItemsStore = useEventListItemsStore();
  const projectIdRef = computed(() => unref(projectId));
  const moduleIdRef = computed(() => unref(moduleId));
  watch(
    moduleIdRef,
    (id) => {
      if (!id) return;
      eventListItemsStore.fetchEventListItemsByModuleId(id);
    },
    { immediate: true },
  );

  const eventItemsOptions = computed(() => {
    const moduleIdValue = moduleIdRef.value;
    const projectIdValue = projectIdRef.value;
    if (!moduleIdValue || !projectIdValue) return [];
    const { orderedEventListItems } = storeToRefs(eventListItemsStore);

    return createEventItemsBreadcrumbDropdownOptions(orderedEventListItems.value, projectIdValue);
  });

  return { eventItemsOptions };
}

export function useNewsBreadcrumbOptions(
  moduleId: MaybeRef<number | undefined>,
  projectId: MaybeRef<number | undefined>,
) {
  const newsItemsStore = useNewsItemsStore();
  const projectIdRef = computed(() => unref(projectId));
  const moduleIdRef = computed(() => unref(moduleId));
  watch(
    moduleIdRef,
    (id) => {
      if (!id) return;
      newsItemsStore.fetchNewsListItemsByModuleId(id);
    },
    { immediate: true },
  );

  const newsItemsOptions = computed(() => {
    const moduleIdValue = moduleIdRef.value;
    const projectIdValue = projectIdRef.value;
    if (!moduleIdValue || !projectIdValue) return [];
    const { orderedNewsListItems } = storeToRefs(newsItemsStore);
    return createNewsItemsBreadcrumbDropdownOptions(orderedNewsListItems.value, projectIdValue);
  });

  return { newsItemsOptions };
}
